import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { getSeoOverview, inspectUrl } from "@/lib/seo.functions";
import { SeoCheckPanel } from "@/components/SeoCheckPanel";
import { Loader2, Search, TrendingUp, MousePointerClick, Eye, Target, ExternalLink, CheckCircle2, AlertTriangle } from "lucide-react";

export const Route = createFileRoute("/admin/seo")({
  component: AdminSeoPage,
});

function AdminSeoPage() {
  return (
    <div className="space-y-10">
      <AdminSeo />
      <SeoCheckPanel />
    </div>
  );
}

function fmtPct(v: number) {
  return `${(v * 100).toFixed(1)}%`;
}

function AdminSeo() {
  const fetchSeo = useServerFn(getSeoOverview);
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-seo-overview"],
    queryFn: () => fetchSeo(),
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground py-12 justify-center">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading Google Search Console data…
      </div>
    );
  }

  if (error) {
    return (
      <p className="text-sm text-destructive py-8">
        Failed to load SEO data: {(error as Error).message}
      </p>
    );
  }

  if (!data?.connected) {
    return (
      <div className="border border-dashed border-border rounded-lg p-8 text-center max-w-lg mx-auto">
        <Search className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
        <h2 className="font-medium mb-1">Google Search Console not connected</h2>
        <p className="text-sm text-muted-foreground">
          Connect your Google Search Console account (in the Lovable chat) to see clicks,
          impressions, rankings and top queries for julsskin.shop.
        </p>
      </div>
    );
  }

  if (!data.site || ("error" in data && data.error)) {
    return <p className="text-sm text-muted-foreground py-8">{("error" in data && data.error) || "No data available."}</p>;
  }

  const stats = [
    { label: "Total clicks", value: data.totals.clicks.toLocaleString(), icon: MousePointerClick },
    { label: "Impressions", value: data.totals.impressions.toLocaleString(), icon: Eye },
    { label: "Avg CTR", value: fmtPct(data.totals.ctr), icon: TrendingUp },
    { label: "Avg position", value: data.totals.position.toFixed(1), icon: Target },
  ];

  const rankedKeywords = [...data.queries]
    .filter((q) => q.impressions > 0)
    .sort((a, b) => a.position - b.position)
    .slice(0, 20);

  return (
    <div className="space-y-8">
      <div>
        <h1 className="display text-2xl mb-1">Google Rank</h1>
        <p className="text-xs text-muted-foreground">
          {data.site} · {data.range.startDate} → {data.range.endDate} (last 28 days)
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {stats.map((s) => (
          <div key={s.label} className="border border-border rounded-lg p-4">
            <div className="flex items-center gap-2 text-muted-foreground text-xs mb-2">
              <s.icon className="h-3.5 w-3.5" /> {s.label}
            </div>
            <p className="text-2xl font-semibold">{s.value}</p>
          </div>
        ))}
      </div>

      <IndexingPanel />

      <section>

        <h2 className="font-medium mb-3 flex items-center gap-2">
          <Target className="h-4 w-4" /> Keyword rankings (best position)
        </h2>
        {rankedKeywords.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No ranking data yet — Google needs a few days after indexing.
          </p>
        ) : (
          <div className="border border-border rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-muted-foreground border-b border-border">
                  <th className="px-3 py-2 font-medium w-12">#</th>
                  <th className="px-3 py-2 font-medium">Keyword</th>
                  <th className="px-3 py-2 font-medium text-right">Position</th>
                  <th className="px-3 py-2 font-medium text-right">Clicks</th>
                  <th className="px-3 py-2 font-medium text-right">Impr.</th>
                  <th className="px-3 py-2 font-medium text-right">CTR</th>
                </tr>
              </thead>
              <tbody>
                {rankedKeywords.map((k, i) => (
                  <tr key={k.query} className="border-b border-border last:border-0">
                    <td className="px-3 py-2 text-muted-foreground">{i + 1}</td>
                    <td className="px-3 py-2 max-w-[260px] truncate" title={k.query}>{k.query}</td>
                    <td className="px-3 py-2 text-right font-medium">{k.position.toFixed(1)}</td>
                    <td className="px-3 py-2 text-right">{k.clicks.toLocaleString()}</td>
                    <td className="px-3 py-2 text-right">{k.impressions.toLocaleString()}</td>
                    <td className="px-3 py-2 text-right">{fmtPct(k.ctr)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <div className="grid lg:grid-cols-2 gap-8">
        <section>
          <h2 className="font-medium mb-3">Top queries (by clicks)</h2>
          <SeoTable
            rows={data.queries.map((q) => ({ key: q.query, ...q }))}
            keyLabel="Query"
          />
        </section>
        <section>
          <h2 className="font-medium mb-3">Top pages</h2>
          <SeoTable
            rows={data.pages.map((p) => ({
              key: p.page.replace(/^https?:\/\/[^/]+/, "") || "/",
              ...p,
            }))}
            keyLabel="Page"
          />
        </section>
      </div>
    </div>
  );
}

function IndexingPanel() {
  const fetchInspect = useServerFn(inspectUrl);
  const [url, setUrl] = useState("https://julsskin.shop/");
  const mut = useMutation({
    mutationFn: (u: string) => fetchInspect({ data: { url: u } }),
  });

  const verdictTone = (v?: string | null) => {
    if (v === "PASS") return "text-emerald-600";
    if (v === "FAIL" || v === "NEUTRAL") return "text-amber-600";
    return "text-muted-foreground";
  };

  // Google's official inspection deep-link is returned by the API after a check.
  // Falling back to the generic GSC inspect URL works only when the user is signed in.
  const requestIndexLink =
    mut.data?.inspectionResultLink ??
    `https://search.google.com/search-console/inspect?resource_id=${encodeURIComponent(
      "sc-domain:julsskin.shop",
    )}&id=${encodeURIComponent(url)}`;

  return (
    <section className="border border-border rounded-lg p-4 space-y-3">
      <div className="flex items-center gap-2">
        <Search className="h-4 w-4" />
        <h2 className="font-medium">Google indexing</h2>
      </div>
      <p className="text-xs text-muted-foreground">
        Step 1: paste a URL and click <b>Check status</b>. Step 2: click <b>Request indexing</b> — this opens Google Search Console's inspection page where you press the "Request indexing" button (Google requires this final click on their site; it can't be fully automated).
      </p>
      <form
        className="flex flex-col sm:flex-row gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (url) mut.mutate(url);
        }}
      >
        <input
          type="url"
          required
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://julsskin.shop/some-page"
          className="flex-1 border border-border rounded-md px-3 py-2 text-sm bg-background"
        />
        <button
          type="submit"
          disabled={mut.isPending}
          className="inline-flex items-center justify-center gap-2 rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm disabled:opacity-60"
        >
          {mut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          Check status
        </button>
        <a
          href={requestIndexLink}
          target="_blank"
          rel="noreferrer"
          aria-disabled={!mut.data}
          onClick={(e) => {
            if (!mut.data) {
              e.preventDefault();
              if (url) mut.mutate(url);
            }
          }}
          className={`inline-flex items-center justify-center gap-2 rounded-md border border-border px-4 py-2 text-sm hover:bg-muted ${!mut.data ? "opacity-60" : ""}`}
        >
          <ExternalLink className="h-4 w-4" /> Request indexing
        </a>
      </form>


      {mut.error && (
        <p className="text-sm text-destructive flex items-center gap-2">
          <AlertTriangle className="h-4 w-4" /> {(mut.error as Error).message}
        </p>
      )}

      {mut.data && (
        <div className="grid sm:grid-cols-2 gap-2 text-sm pt-2">
          <Row label="Verdict" value={mut.data.verdict} tone={verdictTone(mut.data.verdict)} icon={mut.data.verdict === "PASS" ? CheckCircle2 : AlertTriangle} />
          <Row label="Coverage" value={mut.data.coverageState ?? "—"} />
          <Row label="Indexing" value={mut.data.indexingState ?? "—"} />
          <Row label="robots.txt" value={mut.data.robotsTxtState ?? "—"} />
          <Row label="Page fetch" value={mut.data.pageFetchState ?? "—"} />
          <Row label="Last crawl" value={mut.data.lastCrawlTime ? new Date(mut.data.lastCrawlTime).toLocaleString() : "Never"} />
          <Row label="Google canonical" value={mut.data.googleCanonical ?? "—"} />
          <Row label="Mobile" value={mut.data.mobileVerdict ?? "—"} tone={verdictTone(mut.data.mobileVerdict)} />
          {mut.data.inspectionResultLink && (
            <a
              href={mut.data.inspectionResultLink}
              target="_blank"
              rel="noreferrer"
              className="sm:col-span-2 inline-flex items-center gap-1 text-primary text-xs hover:underline"
            >
              Open in Google Search Console <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
      )}
    </section>
  );
}

function Row({
  label,
  value,
  tone,
  icon: Icon,
}: {
  label: string;
  value: string;
  tone?: string;
  icon?: React.ComponentType<{ className?: string }>;
}) {
  return (
    <div className="flex items-center justify-between border-b border-border/60 py-1.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className={`flex items-center gap-1 font-medium ${tone ?? ""}`}>
        {Icon ? <Icon className="h-3.5 w-3.5" /> : null}
        <span className="truncate max-w-[200px]" title={value}>{value}</span>
      </span>
    </div>
  );
}



function SeoTable({
  rows,
  keyLabel,
}: {
  rows: { key: string; clicks: number; impressions: number; ctr: number; position: number }[];
  keyLabel: string;
}) {
  if (!rows.length) {
    return <p className="text-sm text-muted-foreground">No data yet — Google needs a few days after indexing.</p>;
  }
  return (
    <div className="border border-border rounded-lg overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-muted-foreground border-b border-border">
            <th className="px-3 py-2 font-medium">{keyLabel}</th>
            <th className="px-3 py-2 font-medium text-right">Clicks</th>
            <th className="px-3 py-2 font-medium text-right">Impr.</th>
            <th className="px-3 py-2 font-medium text-right">CTR</th>
            <th className="px-3 py-2 font-medium text-right">Pos.</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} className="border-b border-border last:border-0">
              <td className="px-3 py-2 max-w-[220px] truncate" title={r.key}>{r.key}</td>
              <td className="px-3 py-2 text-right">{r.clicks.toLocaleString()}</td>
              <td className="px-3 py-2 text-right">{r.impressions.toLocaleString()}</td>
              <td className="px-3 py-2 text-right">{fmtPct(r.ctr)}</td>
              <td className="px-3 py-2 text-right">{r.position.toFixed(1)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
